import { useState, useEffect } from 'react';
import QualityPicker from '@/components/ui/quality-picker';
import { motion, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { useCredits } from '@/contexts/CreditsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, Loader2, Building2, Briefcase, Scale, Heart, ShoppingBag, Diamond, Cpu, Check, X, FileText, LayoutGrid, Twitter, Linkedin, Instagram, Facebook, Youtube, Github, Search, ChevronDown, HelpCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Social brand colors for active state ──
const SOCIAL_BRAND_COLORS = {
  twitter:   { bg: 'rgba(29,161,242,0.15)',  border: 'rgba(29,161,242,0.4)',  text: '#1DA1F2' },
  linkedin:  { bg: 'rgba(10,102,194,0.15)',  border: 'rgba(10,102,194,0.4)',  text: '#0A66C2' },
  instagram: { bg: 'rgba(228,64,95,0.15)',   border: 'rgba(228,64,95,0.4)',   text: '#E4405F' },
  facebook:  { bg: 'rgba(24,119,242,0.15)',  border: 'rgba(24,119,242,0.4)',  text: '#1877F2' },
  youtube:   { bg: 'rgba(255,0,0,0.15)',     border: 'rgba(255,0,0,0.4)',     text: '#FF0000' },
  github:    { bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.2)', text: 'var(--orbis-t2)' },
};

// ── AI Model descriptions for tooltip ──
const MODEL_DESCRIPTIONS = {
  'deepseek/deepseek-v3.2': 'Fast, cost-effective default for Free tier',
  'xiaomi/mimo-v2-pro': 'Fast, good for marketing copy',
  'minimax/minimax-m2.7': 'Balanced quality and speed',
  'claude-haiku-4-5': "Claude's fastest model — quick planning and edits",
  'claude-sonnet-4-6': 'High-quality reasoning for nuanced copy and layout',
  'gemini-2.5-flash': 'Fast balanced model for structure and build',
  'gemini-2.5-pro': 'Higher-quality long-context output',
  'gemini-3.1-pro-preview': "Google's most advanced model — Business & Enterprise",
};

// ── Industry Picker — searchable list of 161 industries from products.csv ──
function IndustryPicker({ value, onChange }) {
  const [industries, setIndustries] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/magic/industries`).then(r => setIndustries(r.data)).catch(() => {});
  }, []);

  const filtered = search
    ? industries.filter(ind =>
        ind.name.toLowerCase().includes(search.toLowerCase()) ||
        ind.keywords.toLowerCase().includes(search.toLowerCase())
      )
    : industries;

  const ranked = search ? filtered.sort((a, b) => {
    const aName = a.name.toLowerCase().startsWith(search.toLowerCase()) ? 0 : 1;
    const bName = b.name.toLowerCase().startsWith(search.toLowerCase()) ? 0 : 1;
    return aName - bName;
  }) : filtered;

  const handleSelect = (ind) => {
    onChange(ind.name, { vibe: ind.primary_style });
    setSearch('');
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400 }}>
        Industry / Type *
      </label>
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--orbis-t4)' }} />
          <input
            type="text"
            value={open ? search : value}
            onChange={e => { setSearch(e.target.value); if (!open) setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search 161 industries..."
            className="w-full h-12 pl-10 pr-10 rounded-lg text-base outline-none transition-colors"
            style={{
              background: 'var(--surface-2)',
              border: '0.5px solid var(--orbis-s2)',
              color: 'var(--orbis-t1)',
            }}
            data-testid="industry-input"
          />
          {value && !open && (
            <button onClick={() => { onChange('', null); setOpen(true); setSearch(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-[var(--orbis-t2)] transition-colors" style={{ color: 'var(--orbis-t4)' }}>
              <X className="w-4 h-4" />
            </button>
          )}
          {!value && (
            <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--orbis-t4)' }} />
          )}
        </div>

        {open && (
          <div className="absolute z-50 w-full mt-1 rounded-lg" style={{ background: 'var(--surface-3)', border: '0.5px solid var(--orbis-s2)' }}>
            <div className="max-h-64 overflow-y-auto">
              <div className="p-1">
                {ranked.length === 0 && (
                  <p className="px-3 py-4 text-base text-center" style={{ color: 'var(--orbis-t2)' }}>
                    No match — type freely to use a custom industry
                  </p>
                )}
                {ranked.slice(0, 30).map((ind, i) => {
                  const isTop = search && i < 3;
                  return (
                    <button key={ind.name} onClick={() => handleSelect(ind)}
                      className="w-full text-left px-3 py-2.5 rounded-lg transition-colors"
                      style={{ background: ind.name === value ? 'var(--surface-2)' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background = ind.name === value ? 'var(--surface-2)' : 'transparent'}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-0.5 h-8 rounded-full shrink-0`} style={{
                          background: isTop ? 'var(--orbis-t2)' : search ? 'var(--orbis-t4)' : 'transparent'
                        }} />
                        <div className="flex-1 min-w-0">
                          <span className="text-base" style={{ color: 'var(--orbis-t1)' }}>{ind.name}</span>
                          {ind.primary_style && (
                            <span className="ml-2 px-1.5 py-0.5 rounded" style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontWeight: 400, background: 'rgba(255,255,255,0.04)' }}>
                              {ind.primary_style.split('+')[0].trim()}
                            </span>
                          )}
                        </div>
                      </div>
                      {ind.landing_pattern && (
                        <p className="mt-0.5 ml-3 truncate" style={{ fontSize: '13px', color: 'var(--orbis-t2)' }}>{ind.landing_pattern}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {search && !ranked.some(r => r.name.toLowerCase() === search.toLowerCase()) && (
              <button onClick={() => { onChange(search, null); setSearch(''); setOpen(false); }}
                className="w-full px-3 py-2.5 text-left text-base transition-colors"
                style={{ color: '#818cf8', borderTop: '0.5px solid var(--orbis-s2)' }}>
                Use "{search}" as custom industry
              </button>
            )}
          </div>
        )}
      </div>

      {open && <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); if (!value && search) onChange(search, null); setSearch(''); }} />}
    </div>
  );
}

const PRESETS = [
  { id: 'saas', label: 'SaaS', icon: Cpu },
  { id: 'agency', label: 'Agency', icon: Briefcase },
  { id: 'law_firm', label: 'Law Firm', icon: Scale },
  { id: 'med_spa', label: 'Med Spa', icon: Heart },
  { id: 'ecommerce', label: 'E-commerce', icon: ShoppingBag },
  { id: 'luxury', label: 'Luxury', icon: Diamond },
  { id: 'ai_startup', label: 'AI Startup', icon: Sparkles },
  { id: 'restaurant', label: 'Restaurant', icon: Building2 },
];

const PAGE_OPTIONS = ['home', 'about', 'services', 'features', 'pricing', 'contact', 'work', 'team', 'blog', 'faq'];

const SOCIAL_PLATFORMS = [
  { id: 'twitter', label: 'X / Twitter', icon: Twitter },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { id: 'instagram', label: 'Instagram', icon: Instagram },
  { id: 'facebook', label: 'Facebook', icon: Facebook },
  { id: 'youtube', label: 'YouTube', icon: Youtube },
  { id: 'github', label: 'GitHub', icon: Github },
];

// Model selection replaced by QualityPicker — backend picks optimal model from pools.

export default function BriefStage({ brief, brandContext, onUpdate, onContinue }) {
  const prefersReducedMotion = useReducedMotion();
  const { tier } = useCredits();
  const voice = brandContext?.brand_voice;
  const contacts = brandContext?.contact_hints;

  // availableModels removed — QualityPicker handles model selection
  // Auto-detect social platforms from contact_hints URLs (for initial state)
  const _contactSocialLinks = contacts?.social_links
    ? Object.entries(contacts.social_links).filter(([k, v]) => v && SOCIAL_PLATFORMS.find(p => p.id === k)).map(([k]) => k)
    : [];
  const _contactSocialUrls = contacts?.social_links
    ? Object.fromEntries(Object.entries(contacts.social_links).filter(([, v]) => v))
    : {};

  const [formData, setFormData] = useState({
    company_name: brief?.company_name || voice?.company_name || '',
    industry: brief?.industry || voice?.industry || '',
    audience: brief?.audience || voice?.target_audience || '',
    vibe: brief?.vibe || voice?.style_vibe || '',
    pages: brief?.pages || ['home'],
    notes: brief?.notes || voice?.company_description || '',
    preset: brief?.preset || null,
    generation_mode: brief?.generation_mode || 'landing_page',
    model: brief?.model || '',
    social_links: brief?.social_links?.length ? brief.social_links : _contactSocialLinks,
    social_urls: brief?.social_urls || (_contactSocialUrls && Object.keys(_contactSocialUrls).length ? _contactSocialUrls : {}),
    email: brief?.email || contacts?.email || '',
    phone: brief?.phone || contacts?.phone || '',
    location: brief?.location || contacts?.location || '',
    build_strategy: brief?.build_strategy || null,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [presets, setPresets] = useState({});
  const [showModelTooltip, setShowModelTooltip] = useState(false);

  // Sync brand voice + contact data into empty form fields (safety net for timing edge cases)
  useEffect(() => {
    if (!voice && !contacts) return;
    setFormData(prev => {
      const contactSocials = contacts?.social_links
        ? Object.entries(contacts.social_links).filter(([k, v]) => v && SOCIAL_PLATFORMS.find(p => p.id === k)).map(([k]) => k)
        : [];
      const contactSocialUrls = contacts?.social_links
        ? Object.fromEntries(Object.entries(contacts.social_links).filter(([, v]) => v))
        : {};
      return {
        ...prev,
        company_name: prev.company_name || voice?.company_name || '',
        industry: prev.industry || voice?.industry || '',
        audience: prev.audience || voice?.target_audience || '',
        vibe: prev.vibe || voice?.style_vibe || '',
        notes: prev.notes || voice?.company_description || '',
        email: prev.email || contacts?.email || '',
        phone: prev.phone || contacts?.phone || '',
        location: prev.location || contacts?.location || '',
        social_links: prev.social_links.length ? prev.social_links : contactSocials,
        social_urls: Object.keys(prev.social_urls || {}).length ? prev.social_urls : contactSocialUrls,
      };
    });
  }, [voice, contacts]);

  useEffect(() => {
    fetchPresets();
  }, []);

  // Model auto-sync removed — QualityPicker handles model selection

  const fetchPresets = async () => {
    try {
      const response = await axios.get(`${API_URL}/magic/presets`);
      setPresets(response.data.presets);
    } catch (e) {
      console.error('Failed to fetch presets');
    }
  };

  const handlePresetSelect = (presetId) => {
    const preset = presets[presetId];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        preset: presetId,
        industry: preset.industry,
        vibe: preset.vibe,
        audience: preset.audience,
        pages: preset.pages
      }));
    } else {
      setFormData(prev => ({ ...prev, preset: presetId }));
    }
  };

  const togglePage = (page) => {
    setFormData(prev => ({
      ...prev,
      pages: prev.pages.includes(page)
        ? prev.pages.filter(p => p !== page)
        : [...prev.pages, page]
    }));
  };

  const handleContinue = async () => {
    if (!formData.company_name || !formData.industry) {
      return;
    }

    setIsGenerating(true);
    try {
      await onUpdate(formData);
      await onContinue();
    } finally {
      setIsGenerating(false);
    }
  };

  const isValid = formData.company_name && formData.industry && (formData.generation_mode === 'landing_page' || formData.pages.length > 0);

  const stagger = (i) => prefersReducedMotion ? {} : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.04, duration: 0.3, ease: [0.22, 1, 0.36, 1] } };

  return (
    <div className="w-full flex flex-col" style={{ height: 'calc(100vh - 64px)', background: 'var(--surface-0)' }}>

      <ScrollArea className="flex-1 min-h-0">
        <div style={{ padding: '20px 20px 96px 20px' }}>
          {/* Two-column layout — 480px left (config) + flex right (company) */}
          <div className="flex gap-8">

            {/* ═══ LEFT COLUMN — Config & Settings ═══ */}
            <div className="w-[480px] shrink-0 space-y-6" style={{ borderRight: '1px solid #27272A60', paddingRight: 20 }}>
              {/* Quick Start — 2×4 grid */}
              <div>
                <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400, display: 'block', marginBottom: '10px' }}>
                  Quick Start (Optional)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PRESETS.map((preset, idx) => {
                    const Icon = preset.icon;
                    const isSelected = formData.preset === preset.id;
                    return (
                      <motion.button
                        key={preset.id}
                        {...stagger(idx)}
                        onClick={() => handlePresetSelect(preset.id)}
                        className="p-3 rounded-lg text-left transition-colors"
                        style={{
                          height: '68px',
                          background: isSelected ? 'rgba(184,204,223,0.08)' : 'var(--surface-1)',
                          border: isSelected ? '0.5px solid rgba(184,204,223,0.35)' : '0.5px solid var(--orbis-s2)',
                          boxShadow: isSelected ? '0 0 0 1.5px rgba(184,204,223,0.4)' : 'none',
                        }}
                        data-testid={`preset-${preset.id}`}
                      >
                        <Icon className="w-4 h-4 mb-1.5" style={{ color: isSelected ? 'var(--orbis-blue)' : 'var(--orbis-t2)' }} />
                        <span className="block text-[13px] font-medium" style={{ color: isSelected ? 'var(--orbis-blue)' : 'var(--orbis-t2)' }}>
                          {preset.label}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Website Type */}
              <div>
                <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400, display: 'block', marginBottom: '10px' }}>
                  Website Type *
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { id: 'landing_page', label: 'Landing Page', desc: 'Single scrollable page', icon: FileText },
                    { id: 'multi_page', label: 'Multi-Page', desc: 'Separate pages built by AI', icon: LayoutGrid },
                  ].map(type => {
                    const Icon = type.icon;
                    const isSelected = formData.generation_mode === type.id;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setFormData(prev => ({ ...prev, generation_mode: type.id }))}
                        className="flex items-center gap-3 p-3 rounded-lg text-left transition-colors"
                        style={{
                          background: isSelected ? 'rgba(184,204,223,0.08)' : 'var(--surface-1)',
                          border: isSelected ? '0.5px solid rgba(184,204,223,0.35)' : '0.5px solid var(--orbis-s2)',
                        }}
                        data-testid={`mode-${type.id}`}
                      >
                        <Icon className="w-4 h-4 shrink-0" style={{ color: isSelected ? 'var(--orbis-blue)' : 'var(--orbis-t2)' }} />
                        <div>
                          <span className="block text-[14px] font-medium" style={{ color: isSelected ? 'var(--orbis-blue)' : 'var(--orbis-t2)' }}>
                            {type.label}
                          </span>
                          <span className="block text-[12px]" style={{ color: 'rgba(240,239,232,0.4)' }}>
                            {type.desc}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Pages — only for multi-page mode */}
              {formData.generation_mode === 'multi_page' && (
                <div className="space-y-2">
                  <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400 }}>
                    Pages to Include
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {PAGE_OPTIONS.map((page) => {
                      const isSelected = formData.pages.includes(page);
                      return (
                        <button
                          key={page}
                          onClick={() => togglePage(page)}
                          className="flex items-center gap-1.5 px-3 rounded-full text-[13px] font-medium transition-all"
                          style={{
                            height: '28px',
                            background: isSelected ? 'rgba(184,204,223,0.1)' : 'var(--surface-2)',
                            border: isSelected ? '0.5px solid rgba(184,204,223,0.35)' : '0.5px solid var(--orbis-s2)',
                            color: isSelected ? 'var(--orbis-blue)' : 'var(--orbis-t2)',
                          }}
                          data-testid={`page-${page}`}
                        >
                          {isSelected && (
                            <motion.div
                              initial={prefersReducedMotion ? false : { opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -8 }}
                            >
                              <Check className="w-3 h-3" />
                            </motion.div>
                          )}
                          {page.charAt(0).toUpperCase() + page.slice(1)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Generation Quality — replaces raw model picker */}
              <QualityPicker
                tier={tier}
                value={formData.model}
                onChange={(val) => setFormData(prev => ({ ...prev, model: val }))}
              />
            </div>

            {/* ═══ RIGHT COLUMN — Company Details ═══ */}
            <div className="flex-1 min-w-0 space-y-6">
              {/* Header */}
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 300, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)', letterSpacing: -0.02 }}>Tell us about your project</h2>
                <p style={{ fontSize: 13, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', marginTop: 4 }}>We'll create a website tailored to your needs</p>
              </div>
              {/* Company Name */}
              <div className="space-y-2">
                <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400 }}>
                  Company Name *
                </label>
                <Input
                  placeholder="Enter company name"
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  className="h-12 text-base"
                  style={{ background: 'var(--surface-2)', border: '0.5px solid var(--orbis-s2)', color: 'var(--orbis-t1)' }}
                  data-testid="company-name-input"
                />
              </div>

              {/* Two-col row: Industry + Target Audience */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <IndustryPicker
                  value={formData.industry}
                  onChange={(industry, suggestion) => {
                    setFormData(prev => ({
                      ...prev,
                      industry,
                      ...(suggestion?.vibe && !prev.vibe ? { vibe: suggestion.vibe } : {}),
                    }));
                  }}
                />
                <div className="space-y-2">
                  <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400 }}>
                    Target Audience
                  </label>
                  <Input
                    placeholder="Who is this website for?"
                    value={formData.audience}
                    onChange={(e) => setFormData(prev => ({ ...prev, audience: e.target.value }))}
                    className="h-12 text-base"
                    style={{ background: 'var(--surface-2)', border: '0.5px solid var(--orbis-s2)', color: 'var(--orbis-t1)' }}
                    data-testid="audience-input"
                  />
                </div>
              </div>

              {/* Style / Vibe */}
              <div className="space-y-2">
                <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400 }}>
                  Style / Vibe
                </label>
                <Textarea
                  placeholder="e.g. Glassmorphism + Flat Design"
                  value={formData.vibe}
                  onChange={(e) => setFormData(prev => ({ ...prev, vibe: e.target.value }))}
                  className="text-base min-h-[80px]"
                  style={{ background: 'var(--surface-2)', border: '0.5px solid var(--orbis-s2)', color: 'var(--orbis-t1)' }}
                  data-testid="vibe-input"
                />
              </div>

              {/* Business Info */}
              <div className="space-y-2">
                <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400 }}>
                  Business Info
                </label>
                <Textarea
                  placeholder="Any specific requirements or preferences"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="text-base min-h-[100px]"
                  style={{ background: 'var(--surface-2)', border: '0.5px solid var(--orbis-s2)', color: 'var(--orbis-t1)' }}
                  data-testid="notes-input"
                />
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400 }}>
                  Contact Info <span style={{ fontSize: 10, color: 'var(--orbis-t4)', textTransform: 'none', letterSpacing: 0 }}>(optional — used in footer &amp; contact sections)</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    type="email"
                    placeholder="hello@yourcompany.com"
                    value={formData.email || ''}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="h-9 px-3 text-sm rounded-lg outline-none transition-colors"
                    style={{ background: 'var(--surface-2)', border: '0.5px solid var(--orbis-s2)', color: 'var(--orbis-t1)' }}
                  />
                  <input
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone || ''}
                    onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="h-9 px-3 text-sm rounded-lg outline-none transition-colors"
                    style={{ background: 'var(--surface-2)', border: '0.5px solid var(--orbis-s2)', color: 'var(--orbis-t1)' }}
                  />
                  <input
                    type="text"
                    placeholder="City, State or Country"
                    value={formData.location || ''}
                    onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    className="h-9 px-3 text-sm rounded-lg outline-none transition-colors"
                    style={{ background: 'var(--surface-2)', border: '0.5px solid var(--orbis-s2)', color: 'var(--orbis-t1)' }}
                  />
                </div>
              </div>

              {/* Social Links — brand-colored pills */}
              <div className="space-y-3">
                <label style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontWeight: 400 }}>
                  Social Links
                </label>
                <div className="flex flex-wrap gap-2">
                  {SOCIAL_PLATFORMS.map(({ id, label, icon: Icon }) => {
                    const isActive = formData.social_links.includes(id);
                    const brandColor = SOCIAL_BRAND_COLORS[id];
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          social_links: isActive
                            ? prev.social_links.filter(s => s !== id)
                            : [...prev.social_links, id]
                        }))}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all"
                        style={{
                          background: isActive ? brandColor.bg : 'var(--surface-2)',
                          border: `0.5px solid ${isActive ? brandColor.border : 'var(--orbis-s2)'}`,
                          color: isActive ? brandColor.text : 'var(--orbis-t2)',
                        }}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </button>
                    );
                  })}
                </div>
                {formData.social_links.length > 0 && (
                  <div className="space-y-2 pt-1">
                    {formData.social_links.map(id => {
                      const platform = SOCIAL_PLATFORMS.find(p => p.id === id);
                      if (!platform) return null;
                      const Icon = platform.icon;
                      return (
                        <div key={id} className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--orbis-t4)' }} />
                          <input
                            type="url"
                            placeholder={`https://${id === 'twitter' ? 'x.com' : id + '.com'}/yourprofile`}
                            value={(formData.social_urls || {})[id] || ''}
                            onChange={e => setFormData(prev => ({
                              ...prev,
                              social_urls: { ...(prev.social_urls || {}), [id]: e.target.value }
                            }))}
                            className="flex-1 h-8 px-3 text-sm rounded-lg outline-none transition-colors"
                            style={{ background: 'var(--surface-2)', border: '0.5px solid var(--orbis-s2)', color: 'var(--orbis-t1)' }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Bottom Action Bar */}
      <div className="magic-bottom-bar">
        {/* Previous — hidden on step 1 */}
        <div style={{ width: '150px' }} />
        <span style={{ fontSize: '11px', color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)', letterSpacing: '0.5px' }}>
          Brief · Step 1 of 7
        </span>
        {/* CTA with glow ring */}
        <div className="orbis-cta-ring rounded-full p-0.5">
          <button
            onClick={handleContinue}
            disabled={!isValid || isGenerating}
            className="orbis-cta relative overflow-hidden transition-all rounded-full"
            style={{
              minWidth: '240px', height: '44px',
              opacity: isValid && !isGenerating ? 1 : 0.4,
              fontSize: '12px', fontWeight: 500,
              padding: '0 28px', whiteSpace: 'nowrap',
              cursor: isValid && !isGenerating ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--orbis-font)',
            }}
            data-testid="continue-btn"
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating Design System...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                Generate Design System
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
